import { lazy, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { posthog } from '@/lib/posthog';
import { useTheme } from '@/hooks/useTheme';
import { usePageMeta } from '@/hooks/usePageMeta';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Hero } from '@/components/Hero';
import { ProductHuntBadge } from '@/components/ProductHuntBadge';
import { PlatformHome } from '@/components/v2/PlatformHome';
import { positioning, siteDescription } from '@/components/v2/site-content';
const PlatformPage = lazy(() => import('@/components/v2/PlatformPage'));
const EnterprisePage = lazy(() => import('@/components/v2/EnterprisePage'));
const SolutionPage = lazy(() => import('@/components/v2/SolutionPage'));
const ContactPage = lazy(() =>
  import('@/components/ContactPage').then((m) => ({ default: m.ContactPage })),
);
const PrivacyPage = lazy(() =>
  import('@/components/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import('@/components/TermsPage').then((m) => ({ default: m.TermsPage })),
);
const NotFoundPage = lazy(() =>
  import('@/components/NotFoundPage').then((m) => ({
    default: m.NotFoundPage,
  })),
);
function HomePage() {
  usePageMeta({
    title: `Foxet.ai — ${positioning}`,
    description: siteDescription,
    path: '/',
  });
  return (
    <>
      <Hero />
      <PlatformHome />
      <ProductHuntBadge />
    </>
  );
}
function ScrollHandler() {
  const { pathname, hash, key } = useLocation();
  const previousPath = useRef(pathname);
  useEffect(() => {
    const pathChanged = previousPath.current !== pathname;
    previousPath.current = pathname;
    posthog.capture('$pageview');
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (hash) {
      let frame = 0;
      let attempts = 0;
      const scroll = () => {
        let target: HTMLElement | null = null;
        try {
          target = document.getElementById(decodeURIComponent(hash.slice(1)));
        } catch {
          /* Invalid hash: leave the page usable. */
        }
        if (target) {
          target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
          if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
          target.focus({ preventScroll: true });
        } else if (++attempts < 30) frame = requestAnimationFrame(scroll);
      };
      frame = requestAnimationFrame(scroll);
      return () => cancelAnimationFrame(frame);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (!pathChanged) return;
    const frame = requestAnimationFrame(() =>
      document.getElementById('main-content')?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash, key]);
  return null;
}
export function App() {
  const { theme, toggle } = useTheme();
  return (
    <BrowserRouter>
      <ScrollHandler />
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <Navbar theme={theme} onToggleTheme={toggle} />
      <main id="main-content" tabIndex={-1}>
        <Suspense
          fallback={
            <div className="page-loading" role="status">
              Loading Foxet…
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/enterprise" element={<EnterprisePage />} />
            <Route path="/product" element={<SolutionPage kind="product" />} />
            <Route path="/pre-sales" element={<SolutionPage kind="sales" />} />
            <Route
              path="/marketing"
              element={<SolutionPage kind="marketing" />}
            />
            <Route
              path="/training"
              element={<SolutionPage kind="training" />}
            />
            <Route path="/support" element={<SolutionPage kind="support" />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </BrowserRouter>
  );
}
